import { useState } from 'react';

import Button from 'components/Button';
import GdprAccordion from 'components/GdprAccordion';
import Message from 'components/Message';
import {
	ErrorMessageInfoBox,
	Form,
	getValidationFormErrors,
	SubmitButton,
	TextInput,
} from 'components/ReactHookForm';
import { fetchDpApiEndpointResult } from 'endpoints/dpApi';
import { useEngagementGoals } from 'hooks';
import { useNotifyInStockGTM } from 'hooks/gtm';
import type { VariantId } from 'models/product';
import { useI18n } from 'utils/i18n';

interface Props {
	knownCustomerText: string;
	onClose: () => void;
	shouldAskForEmail?: boolean;
	submitButtonText: string;
	successText: string;
	unknownCustomerText: string;
	userEmail?: string;
	variantId: VariantId;
}
interface EmailForm {
	/** Only set when asking for email. */
	email?: string;
}

export default function WatchableSignup({
	knownCustomerText,
	onClose,
	shouldAskForEmail,
	submitButtonText,
	successText,
	unknownCustomerText,
	userEmail = '',
	variantId,
}: Props) {
	const { t } = useI18n();
	const { sendNotifyInStockEvent } = useNotifyInStockGTM();
	const { sendEngagement } = useEngagementGoals();

	const [isSuccess, setIsSuccess] = useState(false);

	const onWatchableSignup = async (formData: EmailForm) =>
		getValidationFormErrors(
			await fetchDpApiEndpointResult({
				name: 'PostWatchVariantStock',
				queryParams: { email: formData.email ?? userEmail, variantId },
			}),
		);

	const onSuccess = () => {
		sendNotifyInStockEvent(variantId);
		sendEngagement({
			type: 'registeredForBackInStock',
		});
		setIsSuccess(true);
	};

	return (
		<>
			{isSuccess && (
				<>
					<Message text={successText} variant="success" className="mb-4" />
					<Button
						variant="secondary"
						displayWidth="full"
						size="lg"
						onClick={onClose}
					>
						{t('general_close_button_label')}
					</Button>
				</>
			)}
			<Form<EmailForm>
				className={isSuccess ? 'hidden' : undefined}
				formLocation="watchable_signup"
				onSubmit={onWatchableSignup}
				onSuccess={onSuccess}
			>
				<ErrorMessageInfoBox className="sticky top-0 z-99 mb-4" />
				{shouldAskForEmail && (
					<>
						<p>{unknownCustomerText}</p>
						<GdprAccordion />
						<TextInput
							id="email"
							name="email"
							label={t('general_email_label')}
							defaultValue={userEmail}
							type="email"
							required
							className="my-4"
						/>
					</>
				)}
				{!shouldAskForEmail && (
					<>
						<p>{knownCustomerText}</p>
						<p className="my-4">{`${t('general_email_label')}: ${userEmail}`}</p>
					</>
				)}
				<SubmitButton>{submitButtonText}</SubmitButton>
			</Form>
		</>
	);
}
WatchableSignup.displayName = 'WatchableSignup';
